// Drag and drop across the board's columns (@dnd-kit), after dnd-kit's
// "multiple containers" pattern: while a card is dragged over another column
// it is moved into that column's list so the cards there make room for it;
// the drop reports the target column and its final order.
import {
  closestCenter,
  closestCorners,
  getFirstCollision,
  KeyboardCode,
  pointerWithin,
  rectIntersection,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type DroppableContainer,
  type KeyboardCoordinateGetter,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { StateSlug } from '../api/types'
import { stateName } from '../vocab'

export type ColumnItems = Readonly<Record<string, readonly string[]>>

const COLUMN_PREFIX = 'column:'

export function columnId(state: string): string {
  return `${COLUMN_PREFIX}${state}`
}

export function isColumnId(id: UniqueIdentifier): boolean {
  return String(id).startsWith(COLUMN_PREFIX)
}

function columnState(id: UniqueIdentifier): string {
  return String(id).slice(COLUMN_PREFIX.length) as string
}

export function findColumn(items: ColumnItems, id: UniqueIdentifier): string | undefined {
  if (isColumnId(id)) {
    const state = columnState(id)
    return items[state] ? state : undefined
  }
  const key = String(id)
  return Object.keys(items).find((state) => items[state]?.includes(key)) as string | undefined
}

/** A column folded to a thin strip (a hidden Cancelled or Duplicate) says so in its droppable's data. */
function isThin(over: { id: UniqueIdentifier; data: { current?: unknown } }): boolean {
  return isColumnId(over.id) && (over.data.current as { thin?: boolean } | undefined)?.thin === true
}

export interface Drop {
  issueId: string
  from: string
  to: string
  /** The target column's ids in display order, the moved card included. */
  orderedIds: readonly string[]
}

/**
 * Arrow keys move a picked-up card to the nearest card (or empty column) in
 * that direction; dnd-kit's example getter for multiple containers.
 */
export const boardKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  { context: { active, droppableRects, droppableContainers, collisionRect, over } },
) => {
  const directions: string[] = [KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left]
  if (!directions.includes(event.code)) return undefined
  event.preventDefault()
  if (!active || !collisionRect) return undefined

  const candidates: DroppableContainer[] = []
  for (const entry of droppableContainers.getEnabled()) {
    if (entry.id === active.id) continue
    const rect = droppableRects.get(entry.id)
    if (!rect) continue
    const data = entry.data.current as { type?: string; count?: number } | undefined
    // A column that holds cards is reached through its cards.
    if (data?.type === 'column' && (data.count ?? 0) > 0) continue
    switch (event.code) {
      case KeyboardCode.Down:
        if (collisionRect.top < rect.top) candidates.push(entry)
        break
      case KeyboardCode.Up:
        if (collisionRect.top > rect.top) candidates.push(entry)
        break
      case KeyboardCode.Left:
        if (collisionRect.left >= rect.left + rect.width) candidates.push(entry)
        break
      case KeyboardCode.Right:
        if (collisionRect.left + collisionRect.width <= rect.left) candidates.push(entry)
        break
    }
  }
  const collisions = closestCorners({
    active,
    collisionRect,
    droppableRects,
    droppableContainers: candidates,
    pointerCoordinates: null,
  })
  let closest = getFirstCollision(collisions, 'id')
  if (closest === over?.id && collisions.length > 1) closest = collisions[1]!.id
  if (closest == null) return undefined
  const target = droppableContainers.get(closest)
  const rect = target?.rect.current
  if (!target?.node.current || !rect) return undefined
  if ((target.data.current as { type?: string } | undefined)?.type === 'column') {
    return { x: rect.left + 12, y: rect.top + 12 }
  }
  return { x: rect.left, y: rect.top }
}

interface DragState {
  activeId: string
  from: string
  /** Display order at pickup, including live promotion; never changes during this drag. */
  base: ColumnItems
  items: ColumnItems
}

export interface BoardDnd {
  /** The column lists to render: the live ones, or the dragged arrangement. */
  items: ColumnItems
  activeId: string | null
  collisionDetection: CollisionDetection
  onDragStart(event: DragStartEvent): void
  onDragOver(event: DragOverEvent): void
  onDragEnd(event: DragEndEvent): void
  onDragCancel(): void
  announcements: Announcements
}

export function useBoardDnd(
  base: ColumnItems,
  onDrop: (drop: Drop) => void,
  describe: (id: UniqueIdentifier) => string,
  nameOf: (key: string) => string = (key) => stateName(key as StateSlug),
): BoardDnd {
  const [drag, setDrag] = useState<DragState | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const lastOverId = useRef<UniqueIdentifier | null>(null)
  const movedToNewColumn = useRef(false)
  // Where the last drop put the card; dnd-kit announces the drop after onDragEnd ran.
  const dropped = useRef<{ column: string; ordered: readonly string[] } | null>(null)
  const items = drag?.items ?? base

  const update = useCallback((next: DragState | null) => {
    dragRef.current = next
    setDrag(next)
  }, [])

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      movedToNewColumn.current = false
    })
    return () => cancelAnimationFrame(frame)
  }, [items])

  const collisionDetection = useCallback<CollisionDetection>(
    (args) => {
      const pointer = pointerWithin(args)
      const intersections = pointer.length > 0 ? pointer : rectIntersection(args)
      let overId = getFirstCollision(intersections, 'id')
      if (overId != null) {
        if (isColumnId(overId)) {
          const ids = new Set(items[columnState(overId)])
          if (ids.size > 0) {
            const inColumn = args.droppableContainers.filter((c) => c.id !== overId && ids.has(String(c.id)))
            overId = closestCenter({ ...args, droppableContainers: inColumn })[0]?.id ?? overId
          }
        }
        lastOverId.current = overId
        return [{ id: overId }]
      }
      if (movedToNewColumn.current && drag) lastOverId.current = drag.activeId
      return lastOverId.current != null ? [{ id: lastOverId.current }] : []
    },
    [items, drag],
  )

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const from = findColumn(base, active.id)
      if (!from) return
      lastOverId.current = null
      update({ activeId: String(active.id), from, base, items: base })
    },
    [base, update],
  )

  const onDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      const current = dragRef.current
      if (!current || !over) return
      const activeColumn = findColumn(current.items, active.id)
      const overColumn = findColumn(current.items, over.id)
      if (!activeColumn || !overColumn || activeColumn === overColumn) return
      // A thin column shows no cards: it takes the drop as it ends.
      if (isThin(over)) return
      const overItems = (current.items[overColumn] ?? [])
      let index: number
      if (isColumnId(over.id)) {
        index = overItems.length
      } else {
        const overIndex = overItems.indexOf(String(over.id))
        const translated = active.rect.current.translated
        const below = translated !== null && translated.top > over.rect.top + over.rect.height / 2
        index = overIndex >= 0 ? overIndex + (below ? 1 : 0) : overItems.length
      }
      movedToNewColumn.current = true
      const id = String(active.id)
      update({
        ...current,
        items: {
          ...current.items,
          [activeColumn]: (current.items[activeColumn] ?? []).filter((i) => i !== id),
          [overColumn]: [...overItems.slice(0, index), id, ...overItems.slice(index)],
        },
      })
    },
    [update],
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const current = dragRef.current
      update(null)
      dropped.current = null
      if (!current || !over) return
      const id = String(active.id)
      // A live move or filter change invalidates the pickup's source column.
      if (findColumn(base, id) !== current.from) return
      const overColumn = findColumn(current.items, over.id)
      const thin = isThin(over) && overColumn !== undefined
      const column = thin ? overColumn : findColumn(current.items, id)
      if (!column) return
      let ordered = (current.items[column] ?? [])
      if (thin) ordered = [...ordered.filter((i) => i !== id), id]
      else if (overColumn === column && !isColumnId(over.id)) {
        const from = ordered.indexOf(id)
        const to = ordered.indexOf(String(over.id))
        if (from >= 0 && to >= 0 && from !== to) ordered = arrayMove([...ordered], from, to)
      }
      dropped.current = { column, ordered }
      const unchanged =
        column === current.from &&
        ordered.length === (current.base[column] ?? []).length &&
        ordered.every((value, i) => value === current.base[column]?.[i])
      if (unchanged) return
      onDrop({ issueId: id, from: current.from, to: column, orderedIds: ordered })
    },
    [base, onDrop, update],
  )

  const onDragCancel = useCallback(() => update(null), [update])

  const announcements = useMemo<Announcements>(() => {
    const where = (id: UniqueIdentifier | undefined) => {
      if (id === undefined) return 'outside the board'
      const state = findColumn(dragRef.current?.items ?? base, id)
      if (!state) return 'outside the board'
      if (isColumnId(id)) return nameOf(state)
      const list = ((dragRef.current?.items ?? base)[state] ?? [])
      return `${nameOf(state)}, position ${list.indexOf(String(id)) + 1} of ${list.length}`
    }
    return {
      onDragStart: ({ active }) => `Picked up ${describe(active.id)}.`,
      onDragOver: ({ active, over }) => `${describe(active.id)} is over ${where(over?.id)}.`,
      onDragEnd: ({ active }) => {
        const drop = dropped.current
        if (!drop) return `${describe(active.id)} was dropped outside the board; nothing changed.`
        const position = drop.ordered.indexOf(String(active.id)) + 1
        return `${describe(active.id)} was dropped in ${nameOf(drop.column)}, position ${position} of ${drop.ordered.length}.`
      },
      onDragCancel: ({ active }) => `Moving ${describe(active.id)} was cancelled.`,
    }
  }, [base, describe, nameOf])

  return {
    items,
    activeId: drag?.activeId ?? null,
    collisionDetection,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    announcements,
  }
}
